import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

interface PatreonNavProps {
  scrolled?: boolean;
}

const PatreonNav: React.FC<PatreonNavProps> = ({ scrolled = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState<string | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstDropdownItemRef = useRef<HTMLAnchorElement>(null);

  // Focus trap for mobile menu
  useEffect(() => {
    if (!isMenuOpen || !mobileMenuRef.current) return;

    const focusableElements = mobileMenuRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      // Close menu on Escape
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      // Focus trap on Tab
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    // Focus first element when menu opens
    firstElement?.focus();

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  // Close dropdowns on Escape and click outside
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && hoveredNavItem) {
        setHoveredNavItem(null);
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      // Close if clicking outside any dropdown trigger or menu
      const target = e.target as HTMLElement;
      const withinTrigger = target.closest('[data-nav-trigger="true"]');
      const withinMenu = target.closest('[data-nav-menu="true"]');
      if (!withinTrigger && !withinMenu && hoveredNavItem) {
        setHoveredNavItem(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hoveredNavItem]);

  // Focus first dropdown item when menu opens
  useEffect(() => {
    if (hoveredNavItem && firstDropdownItemRef.current) {
      firstDropdownItemRef.current.focus();
    }
  }, [hoveredNavItem]);

  // Navigation items with dropdowns (matching Patreon exactly)
  const navItems = [
    {
      label: "Creators",
      link: "/discover",
      dropdown: [
        { label: "Podcasters", link: "/creators/podcasts" },
        { label: "Video creators", link: "/creators/video" },
        { label: "Musicians", link: "/creators/music" },
        { label: "Artists", link: "/creators/visualartists" },
        { label: "Game devs", link: "/creators/gaming" },
      ],
    },
    {
      label: "Features",
      link: "/features",
      dropdown: [
        { label: "Create on your terms", link: "/product/create" },
        {
          label: "Where real community thrives",
          link: "/product/online-community",
        },
        { label: "Grow your community", link: "/product/grow" },
        { label: "Support for your business", link: "/product/manage-business" },
        { label: "Earning made easy", link: "/product/digital-products" },
      ],
    },
    { label: "Pricing", link: "/pricing", dropdown: null },
    { label: "Resources", link: "/resources", dropdown: null },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200"
            : "bg-transparent"
        }`}
        role="navigation"
        aria-label="Primary"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-18" style={{ height: 'var(--patreon-nav-height)' }}>
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold transition-transform group-hover:scale-110"
                style={{ backgroundColor: 'var(--patreon-btn-primary-bg)' }}
              >
                MX
              </div>
              <span className="text-2xl font-bold text-black">MXPatfan</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() =>
                    item.dropdown && setHoveredNavItem(item.label)
                  }
                  onMouseLeave={() => setHoveredNavItem(null)}
                >
                  <Link
                    to={item.link}
                    className="px-4 py-2 text-base font-medium text-gray-800 hover:bg-gray-100 rounded-full transition-colors duration-200 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    aria-haspopup={!!item.dropdown}
                    aria-expanded={item.dropdown ? hoveredNavItem === item.label : undefined}
                    aria-controls={item.dropdown ? `menu-${item.label}` : undefined}
                    data-nav-trigger={item.dropdown ? 'true' : undefined}
                    onClick={(e) => {
                      if (!item.dropdown) return;
                      // Toggle dropdown instead of navigating
                      e.preventDefault();
                      setHoveredNavItem(hoveredNavItem === item.label ? null : item.label);
                    }}
                    onFocus={() => {
                      if (item.dropdown) setHoveredNavItem(item.label);
                    }}
                    onKeyDown={(e) => {
                      if (!item.dropdown) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setHoveredNavItem(hoveredNavItem === item.label ? null : item.label);
                      }
                      if (e.key === 'Escape') setHoveredNavItem(null);
                    }}
                  >
                    {item.label}
                    {item.dropdown && (
                      <svg
                        className="ml-1 w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    )}
                  </Link>

                  {/* Dropdown Menu */}
                  {item.dropdown && hoveredNavItem === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-200 py-2 z-50"
                      id={`menu-${item.label}`}
                      role="menu"
                      aria-label={`${item.label} submenu`}
                      data-nav-menu="true"
                    >
                      {item.dropdown.map((dropdownItem, index) => (
                        <Link
                          key={index}
                          ref={index === 0 ? firstDropdownItemRef : null}
                          to={dropdownItem.link}
                          className="block px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors focus:outline-none focus:bg-gray-100 focus:ring-2 focus:ring-inset focus:ring-blue-500"
                          role="menuitem"
                        >
                          {dropdownItem.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>

            {/* Right Side - Search & Actions */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Search */}
              <button 
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" 
                aria-label="Search"
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
              </button>

              {/* Log in */}
              <Link
                to="/login"
                className="px-5 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Log in
              </Link>

              {/* Get Started */}
              <Link
                to="/create"
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-full transition-all duration-200 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                style={{ background: '#FF424D' }}
              >
                Get Started
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              ref={menuButtonRef}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-lg"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white border-t border-gray-200"
              role="region"
              aria-label="Mobile menu"
            >
              <div className="px-6 py-4 space-y-2">
                {navItems.map((item) => (
                  <div key={item.label}>
                    <Link
                      to={item.link}
                      className="block py-3 text-base font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                    {item.dropdown && (
                      <div className="pl-4 space-y-2">
                        {item.dropdown.map((dropdownItem, index) => (
                          <Link
                            key={index}
                            to={dropdownItem.link}
                            className="block py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 rounded-md"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {dropdownItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className="pt-4 space-y-2">
                  <Link
                    to="/login"
                    className="block w-full text-center py-3 text-sm font-semibold text-gray-800 border-2 border-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Log in
                  </Link>
                  <Link
                    to="/create"
                    className="block w-full text-center py-3 text-sm font-semibold text-white rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #F968A6 100%)' }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default PatreonNav;
